/**
 * <collection-links> — every internal collection link carries the default in-stock filter
 * (?filter.v.availability=1) before it is followed, so a click never lands on the unfiltered page
 * and the availability redirect in layout/theme.liquid (a second full page load, seen as a blank /
 * black flash) never runs for an internal navigation. The menu already builds its links this way
 * (snippets/collection-link.liquid); this covers the rest (hero, collection cards, banners, footer,
 * links added later by search / filters). Same rules as the redirect:
 * - same site, /collections/<handle> only (not product URLs inside a collection, not /collections);
 * - left alone when the URL already has the parameter;
 * - left alone when the shopper chose "show all" for that collection this session
 *   (sessionStorage 'coll-show-all:<path>', written by assets/CollectionFilter.js);
 * - never on links to the collection being shown (filters, filter chips, sort, pages: they carry the
 *   shopper's current choice — "remove availability" must stay removable) or inside <collection-filters>.
 * Rendered once per page (layout/theme.liquid).
 */
if (!customElements.get('collection-links')) {
  const PARAM = 'filter.v.availability';
  const COLLECTION_PATH = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[^/]+\/?$/i;

  const samePath = (a, b) => a.replace(/\/$/, '') === b.replace(/\/$/, '');

  const withFilter = (href) => {
    let url;
    try {
      url = new URL(href, window.location.href);
    } catch (e) {
      return null;
    }
    if (url.origin !== window.location.origin || !COLLECTION_PATH.test(url.pathname) || url.searchParams.has(PARAM)) return null;
    if (samePath(url.pathname, window.location.pathname)) return null; // the page's own state links
    try {
      if (sessionStorage.getItem('coll-show-all:' + url.pathname) === '1') return null;
    } catch (e) {
      // no sessionStorage: the default applies
    }
    url.searchParams.set(PARAM, '1');
    return url.href;
  };

  class CollectionLinks extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.fixAll(document);
      // Links that arrive later (search results, filter re-renders, the menu, quick views).
      this.observer = new MutationObserver((records) => {
        for (const record of records) {
          record.addedNodes.forEach((node) => {
            if (node.nodeType === 1) this.fixAll(node);
          });
        }
      });
      this.observer.observe(document.body, { childList: true, subtree: true });
      // Last line: a link changed after we saw it (or built on the fly) is fixed at the moment it is used.
      const onUse = (event) => {
        const link = event.target.closest?.('a[href]');
        if (link) this.fix(link);
      };
      document.addEventListener('pointerdown', onUse, { capture: true, passive: true, signal });
      document.addEventListener('click', onUse, { capture: true, signal });
      document.addEventListener('keydown', (event) => { if (event.key === 'Enter') onUse(event); }, { capture: true, signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }

    fixAll(root) {
      if (root.matches?.('a[href*="/collections/"]')) this.fix(root);
      root.querySelectorAll?.('a[href*="/collections/"]').forEach((link) => this.fix(link));
    }

    fix(link) {
      if (link.closest('collection-filters')) return;
      const href = link.getAttribute('href');
      if (!href || !href.includes('/collections/')) return;
      const next = withFilter(href);
      if (next) link.setAttribute('href', next);
    }
  }

  customElements.define('collection-links', CollectionLinks);
}
